import { useEffect, useState, ChangeEventHandler, FormEventHandler } from "react";
import clsx from "clsx";
import { resetPassword } from "aws-amplify/auth";
import { useParams } from "react-router-dom";

// Components
import LoginContainer from "@/Auth/Layouts/LoginContainer";
import DelayedButton from "@/Auth/components/DelayedButton";

// MUI
import { Button, CircularProgress, Stack, Typography, TextField, InputAdornment, IconButton } from "@mui/material";

// Icons
import Email from "@/Auth/assets/icons/email.svg";

export default function ResetPassword() {
	const { userName } = useParams();

	const [errorMessage, setErrorMessage] = useState("");

	const [state, setState] = useState({
		username: userName ?? "",
		code: "",
		password: "",
		password2: "",
		display: "none",
		passwordStrength: "",
		someEmptyField: true,
		someEmptyField2: true,
		remember: false,
		showPassword: false,
		isLoading: false,
		showPassword2: false,
		error: false,
		visible: false,
	});

	const handleChangeUser: ChangeEventHandler<HTMLInputElement> = (e) => {
		let someEmptyField = true;
		if (e.target.value) {
			someEmptyField = false;
		}

		setState({
			...state,
			someEmptyField,
			username: e.target.value,
		});
	};

	const forgotPassword: FormEventHandler = (e) => {
		e.preventDefault();
		//Send confirmation code to user's email
		setState((oldValues) => ({
			...oldValues,
			isLoading: true,
		}));
		resetPassword({ username: state.username })
			.then(() => {
				setState((oldValues) => ({
					...oldValues,
					isLoading: false,
					display: "block",
				}));
			})
			.catch((err: Error) => {
				// Display the error message to the user
				if (err.message !== "Username/client id combination not found.") {
					let internalErrorMessage = err.message;
					if (err.message === "User password cannot be reset in the current state.") {
						internalErrorMessage = "Account activation required before password reset.";
					}
					if (err.message === "Attempt limit exceeded, please try after some time.") {
						internalErrorMessage = "Attempt limit exceeded, please try again at a later time.";
					}
					setErrorMessage(internalErrorMessage);
					setState((oldValues) => ({
						...oldValues,
						isLoading: false,
						error: true,
					}));
				} else {
					// in order to prevent the user from knowing if the email exists or not we will display the same message as it was successful
					setState((oldValues) => ({
						...oldValues,
						isLoading: false,
						display: "block",
					}));
				}
			});
	};

	const resendHandler = () => {
		setState((oldValues) => ({
			...oldValues,
			display: "none",
		}));
	};

	useEffect(() => {
		if (userName !== undefined) {
			setState((oldValues) => ({
				...oldValues,
				username: userName,
				someEmptyField: false,
			}));
		}

		return () => {
			setErrorMessage("");
			setState((oldValues) => ({
				...oldValues,
				username: "",
				code: "",
				password: "",
				password2: "",
				display: "none",
				passwordStrength: "",
				isLoading: false,
				someEmptyField: true,
				someEmptyField2: true,
				remember: false,
				showPassword: false,
				showPassword2: false,
				visible: false,
				error: false,
			}));
		};
	}, [userName]);

	return (
		<LoginContainer>
			<form
				className={clsx({
					"form-login form-custom": true,
					"form-error": state.error,
				})}
				noValidate
				onSubmit={forgotPassword}
			>
				{state.display === "none" ? (
					<>
						<Stack direction="column" className="mx-[13px]">
							<Typography className="text-regular ml-[15px] mb-[10px]">Enter your email to reset your password.</Typography>
							<TextField
								placeholder="E-mail"
								variant="outlined"
								fullWidth
								required={true}
								className="mt-0"
								value={state.username}
								onChange={handleChangeUser}
								InputProps={{
									className: "text-regular h-[37px] pl-[14px] pb-[4px] mt-0 rounded-[6px]",
									startAdornment: (
										<InputAdornment position="start" className="pl-[6px] mr-[15px]">
											<IconButton edge="start" disabled>
												{<img src={Email} alt="Email Icon" />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Stack>

						<Stack
							direction="row"
							justifyContent="center"
							alignContent="center"
							className="text-regular text-red-error mt-[10px] min-h-[30px]"
						>
							<span className="alert-message">{errorMessage}</span>
						</Stack>

						<Stack direction="row" justifyContent="center" alignContent="center" className="mx-[13px] mt-[8px]">
							<Button
								variant="contained"
								className="btn-submit"
								type="submit"
								disabled={state.someEmptyField || state.isLoading}
								disableElevation
							>
								{state.isLoading ? <CircularProgress color="inherit" size={25} /> : "Send E-mail"}
							</Button>
						</Stack>
					</>
				) : (
					<Stack className="ml-[78px] w-[359px]">
						<Typography className="text-regular">
							Your submission has been received. If we have an account matching your email address, you will receive an email
							with a link to reset your password.
						</Typography>

						<Stack direction="row" alignItems={"center"} className="mt-[14px]">
							<Typography className="text-regular">Didn't receive the email?</Typography>
							<DelayedButton
								className="ml-[20px] capitalize text-regular"
								countFrom={30}
								clickHandler={resendHandler}
								text={"Try again"}
							>
								Try again
							</DelayedButton>
						</Stack>
					</Stack>
				)}
			</form>
		</LoginContainer>
	);
}
